import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { ColumnDef, PaginationState } from "@tanstack/react-table";
import { grace, PAGE_SIZES } from "@alm/domain";
import { EmptyState } from "@/components/app/lists/empty-state";
import { FilterChip } from "@/components/app/lists/filter-chip";
import { PageHeader } from "@/components/app/shell/page-header";

import { DataTable } from "@/components/app/patterns/data-table";
import { ListToolbar } from "@/components/app/patterns/list-toolbar";
import { RelativeTime } from "@/components/app/patterns/relative-time";
import { RowActions } from "@/components/app/patterns/row-actions";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useFacility } from "@/lib/facility/facility";
import { ArrowLeft, Check, CheckCircle2 } from "@/lib/icons";
import { orpc } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { optionalInt, optionalOneOf, searchPatch } from "@/lib/search";
import { guardRoute } from "@/lib/policy/route-guard";
import { useZoneFor } from "@/lib/facility/zone";
import { keepWhileSame } from "@/lib/query/keep-while-same";
import { AlertKindLabel } from "../-components/alert-kind-label";
import { alertState, type GraceAlert } from "../-components/grace-alert-kind";
import { ResolveAlertDialog } from "../-components/resolve-alert-dialog";
import { useAlertActions } from "../-components/use-alert-actions";
import { useKioskNames } from "../-components/use-kiosk-names";

export const Route = createFileRoute("/_authenticated/_visitors/visitors/grace/alerts")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { status?: "resolved" | "all"; page?: number; size?: number } => ({
    status: optionalOneOf(["resolved", "all"], search.status),
    page: optionalInt(search.page, 2, 100000),
    size: PAGE_SIZES.find((size) => size === Number(search.size)),
  }),
  beforeLoad: ({ context }) => guardRoute(context.me, "visitors"),
  component: GraceAlertsPage,
});

type Status = "active" | "resolved" | "all";

const STATUSES: ReadonlyArray<{ value: Status; label: string }> = [
  { value: "active", label: "Open and seen" },
  { value: "resolved", label: "Resolved" },
  { value: "all", label: "All" },
];
const EMPTY: Record<Status, string> = {
  active: "Nothing needs attention",
  resolved: "No alert has been resolved yet",
  all: "Grace hasn't raised an alert yet",
};

function columns(
  zoneFor: (facilityId?: string | null) => string,
  kioskName: (deviceId: string | null) => string | null,
): ColumnDef<GraceAlert>[] {
  return [
    {
      id: "kind",
      header: "Alert",
      accessorFn: (alert) => alert.kind,
      enableHiding: false,
      enableSorting: false,
      meta: { nowrap: true },
      cell: ({ row: { original: alert } }) => (
        <AlertKindLabel kind={alert.kind} severity={alert.severity} />
      ),
    },
    {
      id: "message",
      header: "What happened",
      accessorFn: (alert) => alert.message,
      enableSorting: false,
      meta: { className: "min-w-64" },
      cell: ({ row: { original: alert } }) => (
        <div className="min-w-0">
          <p className="break-words text-pretty">{alert.message}</p>
          {alert.personId && alert.personName ? (
            <Link
              to="/visitors/people/$personId"
              params={{ personId: alert.personId }}
              className="focus-ring text-muted-foreground rounded-sm text-xs underline underline-offset-2"
            >
              {alert.personName}
            </Link>
          ) : null}
        </div>
      ),
    },
    {
      id: "kiosk",
      header: "Kiosk",
      accessorFn: (alert) => kioskName(alert.kioskDeviceId) ?? "",
      enableSorting: false,
      meta: { priority: 2 },
      cell: ({ row: { original: alert } }) => kioskName(alert.kioskDeviceId) ?? "-",
    },
    {
      id: "raised",
      header: "Raised",
      accessorFn: (alert) => new Date(alert.createdAt).getTime(),
      enableSorting: false,
      meta: { nowrap: true },
      cell: ({ row: { original: alert } }) => (
        <RelativeTime value={alert.createdAt} timeZone={zoneFor(alert.facilityId)} />
      ),
    },
    {
      id: "status",
      header: "Status",
      accessorFn: (alert) => alertState(alert).label,
      enableSorting: false,
      meta: { nowrap: true },
      cell: ({ row: { original: alert } }) => {
        const state = alertState(alert);
        return <Badge variant={state.tone}>{state.label}</Badge>;
      },
    },
  ];
}

/** Every alert Grace and the kiosks raised, the one place staff notifications link to. */
function GraceAlertsPage() {
  const { facilityId } = useFacility();
  const canManage = useCan("visitor:manage", facilityId === "all" ? null : facilityId);
  const zoneFor = useZoneFor();
  const kioskName = useKioskNames(facilityId, canManage);
  const actions = useAlertActions();
  const { status: statusParam, page = 1, size } = Route.useSearch();
  const patch = searchPatch(useNavigate({ from: Route.fullPath }));
  const refine = (next: Partial<ReturnType<typeof Route.useSearch>>) =>
    patch({ page: undefined, ...next });
  const status: Status = statusParam ?? "active";
  const pagination: PaginationState = { pageIndex: page - 1, pageSize: size ?? PAGE_SIZES[0] };
  const setPagination = (next: PaginationState) =>
    next.pageSize !== pagination.pageSize
      ? refine({ size: next.pageSize === PAGE_SIZES[0] ? undefined : next.pageSize })
      : patch({ page: next.pageIndex > 0 ? next.pageIndex + 1 : undefined });
  const alerts = useQuery({
    ...orpc.visitors.grace.alerts.list.queryOptions({
      input: {
        facilityId,
        status,
        limit: pagination.pageSize,
        offset: pagination.pageIndex * pagination.pageSize,
      },
    }),
    placeholderData: keepWhileSame({ facilityId }),
    enabled: canManage,
  });

  const back = (
    <Button asChild variant="ghost" size="sm" className="self-start">
      <Link to="/visitors/grace">
        <ArrowLeft data-icon="inline-start" aria-hidden="true" />
        Grace
      </Link>
    </Button>
  );

  if (!canManage) {
    return (
      <div className="flex flex-col gap-5">
        {back}
        <PageHeader title="Grace's alerts" />
        <Card>
          <EmptyState
            title="Alerts are for the people who run the front door"
            description="Ask an administrator for visitor management access to see and answer them."
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {back}
      <PageHeader
        title="Grace's alerts"
        count={alerts.isSuccess ? alerts.data.total : undefined}
        meta="Held visitors, calls for a person, hosts nobody reached and kiosks that went quiet."
      />
      {alerts.isError ? (
        <Card className="gap-0 py-0">
          <ReadFailed
            className="p-4"
            message="Could not load the alerts."
            error={alerts.error}
            onRetry={() => void alerts.refetch()}
            retrying={alerts.isFetching}
          />
        </Card>
      ) : (
        <DataTable
          data={alerts.data?.rows ?? []}
          columns={columns(zoneFor, kioskName)}
          noun="alerts"
          rowId={(alert) => alert.id}
          pending={alerts.isPending}
          server={{
            rowCount: alerts.data?.total ?? 0,
            pagination,
            onPaginationChange: setPagination,
          }}
          renderToolbar={() => (
            <ListToolbar
              chips={
                <fieldset className="flex min-w-0 flex-wrap gap-1.5">
                  <legend className="sr-only">Filter by status</legend>
                  {STATUSES.map((option) => (
                    <FilterChip
                      key={option.value}
                      selected={status === option.value}
                      onClick={() =>
                        refine({ status: option.value === "active" ? undefined : option.value })
                      }
                    >
                      {option.label}
                    </FilterChip>
                  ))}
                </fieldset>
              }
            />
          )}
          renderRowActions={(alert) => (
            <RowActions
              label={`the ${grace.ALERT_KIND_LABELS[alert.kind].toLowerCase()} alert`}
              modal={false}
              busy={actions.busyAlertId === alert.id}
              primary={
                alert.status === "open" && {
                  label: "Acknowledge",
                  icon: Check,
                  onSelect: () => actions.acknowledge(alert),
                }
              }
              items={[
                alert.status !== "resolved" &&
                  actions.canResolve(alert) && {
                    label: "Resolve",
                    icon: CheckCircle2,
                    onSelect: () => actions.startResolve(alert),
                  },
                alert.sessionId
                  ? {
                      label: "Open the conversation",
                      link: {
                        to: "/visitors/grace/$sessionId",
                        params: { sessionId: alert.sessionId },
                      },
                    }
                  : null,
              ]}
            />
          )}
          empty={<EmptyState title={EMPTY[status]} />}
        />
      )}
      {actions.resolving ? (
        <ResolveAlertDialog alert={actions.resolving} onClose={actions.closeResolve} />
      ) : null}
    </div>
  );
}
